import { Directive, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';

/**
 * Abstract base for every *.api-service.ts under shared/services/api/.
 * Exposes the small set of helpers that all API services duplicate:
 *  - HttpClient injection
 *  - currentLang getter
 *  - HttpParams builder that always appends ?locale=
 *  - HttpHeaders builder for FormData uploads
 *
 * Subclasses must keep the @Injectable({ providedIn: 'root' }) decorator on
 * their own class; @Directive() here is just a TypeScript trick to allow DI
 * fields without requiring a constructor.
 */
@Directive()
export abstract class BaseApiService {
  protected readonly http = inject(HttpClient);
  protected readonly translate = inject(TranslateService);

  protected get currentLang(): string {
    return this.translate.currentLang;
  }

  /**
   * Build an HttpParams instance with `locale` set to currentLang plus any
   * extra entries. Undefined / null values are skipped. Arrays / objects are
   * JSON-stringified — pass them as raw values, no manual JSON.stringify call.
   */
  protected buildParams(
    extra: Record<
      string,
      string | number | boolean | unknown[] | object | undefined | null
    > = {}
  ): HttpParams {
    let params = new HttpParams().set('locale', this.currentLang);
    for (const [key, value] of Object.entries(extra)) {
      if (value === undefined || value === null) {
        continue;
      }
      if (Array.isArray(value) || typeof value === 'object') {
        params = params.set(key, JSON.stringify(value));
      } else {
        params = params.set(key, String(value));
      }
    }
    return params;
  }

  /**
   * Headers for `FormData` uploads: drops `content-type` (browser will set it
   * with the right multipart boundary) and announces JSON in response.
   */
  protected formDataHeaders(
    accept: '*/*' | 'application/json' = 'application/json'
  ): HttpHeaders {
    return new HttpHeaders().delete('content-type').append('Accept', accept);
  }
}
